/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress
} from '@material-ui/core'
import { CloudDownload } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import ElectronicBillService from '../../../services/ElectronicBill'
import Utils from '../../../utils'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

const texts = {
  header: {
    description: 'Lista de notas crédito'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'number',
    headerName: 'Nota crédito',
    widthPct: 12,
    editable: false,
  },
  {
    field: 'date',
    headerName: 'Fecha',
    widthPct: 18,
    editable: false,
  },
  {
    field: 'third',
    headerName: 'Nombre',
    editable: false,
    widthPct: 28,
    cardTitle: true,
  },
  {
    field: 'note',
    headerName: 'Descripción',
    editable: false,
    widthPct: 22,
    hideOnTablet: true,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 20,
    minWidth: 110,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

interface State {
  creditNotes: any[]
  loading: boolean
}

const initState: State = {
  creditNotes: [],
  loading: true
}

function ActionButtons(props: any) {
  const onDownloadFromDian = () => {
    if (props.params.row.qrStr !== undefined) {
      const regex = /https?:\/\/[^\s]+/g;
      const matches = props.params.row.qrStr.match(regex)
      let url = matches ? matches[0] : undefined
      return window.open(url, '_blank')
    } else {
      return toast.warn('Documento disponible ingresando a la visualización')
    }
  }

  const onDownloadPDF = async () => {
    const cude = props.params.row.cude || props.params.row.preview
    if (cude !== undefined) {
      try {
        const electronicBillService = new ElectronicBillService()
        const htmlContent = await electronicBillService.getCreditNoteHTML(cude)

        const documentNumber = props.params.row.number
        Utils.generateCreditNotePDF(htmlContent, documentNumber)

        toast.success('PDF de nota crédito generado correctamente')
      } catch (error) {
        console.error('Error generando PDF de nota crédito:', error)
        toast.error(`Error generando PDF de nota crédito: ${error}`)
      }
    } else {
      toast.error('No hay nota crédito disponible')
    }
  }

  return (
    <React.Fragment>
      <IconButton
        type="button"
        onClick={() => onDownloadPDF()}
        title="Descargar nota crédito"
      >
        <CloudDownload fontSize={"small"} color="primary" />
      </IconButton>
      <IconButton
        type="button"
        onClick={() => onDownloadFromDian()}
        title="Descargar nota crédito desde la DIAN"
      >
        <CloudDownload fontSize={"small"} color="action" />
      </IconButton>
    </React.Fragment>
  )
}

export default function CreditNoteList() {
  const classes = useStyles()
  const [state, setState] = useState<State>(initState)
  const [page, setPage] = useState<number>(1)

  React.useEffect(() => {
    async function loadData() {
      try {
        setState({
          ...state,
          loading: true
        })
        const electronicBillService = new ElectronicBillService()
        const response = await electronicBillService.getCreditNotesFromPlemsi(page)
        let notes: any[] = []

        if (response?.totalDocuments > 0 && Array.isArray(response?.docs)) {
          notes = response.docs.map((doc: any) => ({
            id: doc._id,
            number: doc.consecutive ?? doc.number ?? '-',
            date: `${doc.date ?? ''} ${doc.time ?? ''}`.trim() || '-',
            third: doc.customer?.name ?? '-',
            note: doc.notes ?? (doc.billing_reference?.number ? `Factura ref: ${doc.billing_reference.number}` : '-'),
            preview: doc.preview,
            cude: doc.cude,
            qrStr: doc.QRStr,
            state: doc.state
          }))
        }
        setState({
          ...state,
          creditNotes: notes,
          loading: false
        })
      } catch (error) {
        setState({
          ...state,
          loading: false
        })
        if (axios.isAxiosError(error)) {
          const serverError = error as AxiosError<ServerError>
          if (serverError?.response?.data?.message) {
            return toast.error(serverError.response.data.message)
          }
        }
        toast.error('Error cargando notas crédito')
      }
    }
    loadData()
  }, [page])

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>
        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.creditNotes}
            columns={columns}
            keyId="id"
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            onClick={() => setPage(page + 1)}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>

      {state.loading && (
        <div className={classes.loadingContainer}>
          <Box>
            <CircularProgress />
          </Box>
        </div>
      )}
    </PageContainer>
  )
}
